import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BackendPlan, getPlanName, getPlanRate } from '../util/plans';
import { EVENT, makeCustomEvent } from '../util/events';
import { formatCurrency } from '../util/format';
import { consume } from '@lit/context';
import { breakpointContext } from '../contexts/breakpoint';
import { BREAKPOINT } from '../util/breakpoints';
import { classMap } from 'lit/directives/class-map.js';
import { PAGE } from '../util/page';
import { filtersContext } from '../contexts/filters';
import { Filters } from '../util/filter-sort';
import { getAnalyticPageDescription } from '../util/analytics';
import { sourceContext } from '../contexts/source';
import { SOURCE } from '../util/source';

@customElement('rq-compare-bar')
export class CompareBar extends LitElement {
    static styles = css`
        :host {
            position: var(--root-position, fixed);
            display: block;
            bottom: 0;
            width: 100%;
            box-sizing: border-box;
            z-index: 9;
        }
        .wrapper {
            padding: 24px 4%;
            box-sizing: border-box;
            width: 100%;
            display: flex;
            justify-content: space-between;
        }
        .wrapper-mobile {
            gap: 16px;
            padding: 16px;
            flex-direction: column;
        }
        .top-mobile {
            width: 100%;
            display: flex;
            justify-content: space-between;
        }
        .plans,
        .cards {
            display: flex;
            align-items: center;
            gap: 8px;
            flex: 1;
        }
        .cards {
            flex-wrap: wrap;
        }
        .plans-mobile {
            flex-direction: column;
            align-items: start;
        }
        .numbers {
            color: var(--lh-color-neutral-white, #fff);
        }
        .cards {
            width: 100%;
        }
        .plan-card-wrapper {
            max-width: 45%;
        }
        .plan-card {
            box-sizing: border-box;
            display: flex;
            flex-direction: row;
            gap: 8px;
            align-items: center;
            padding: 8px;
            border-radius: 10px;
            border: 2px solid var(--lh-color-neutral-200, #d1d3d4);
            background-color: var(--lh-theme-button-color-filled-background-default);
            color: var(--lh-theme-button-color-filled-content-default);
        }
        .card-title {
            white-space: nowrap;
            color: var(--lh-theme-button-color-filled-content-default);
        }
        .card-title-mobile {
            white-space: pre-wrap;
            color: var(--lh-theme-button-color-filled-content-default);
        }
        .remove-btn {
            background: none;
            border: none;
            cursor: pointer;
            padding: 4px;
            display: flex;
            align-items: center;
            color: inherit;
        }
        .compare-btn {
            margin: auto 0px;
        }
    `;

    @property()
    plans: BackendPlan[] = [];

    @consume({ context: breakpointContext, subscribe: true })
    breakpoint!: BREAKPOINT;

    @consume({ context: filtersContext, subscribe: true })
    filters!: Filters;

    @consume({ context: sourceContext })
    source!: SOURCE;

    connectedCallback() {
        super.connectedCallback();
        this.style.setProperty('--root-position', this.source === SOURCE.EMBEDDED_AGENT ? 'sticky' : 'fixed');
    }

    get planCards() {
        return this.plans.map((plan) => {
            const planName = getPlanName(plan);
            const rate = getPlanRate(plan);
            let name = planName?.split('|')?.[0].trim();
            if (name.length > 23) {
                name = name.substring(0, 20) + '.';
            }
            const price = formatCurrency(rate);
            const title = `${name}${this.isMobile ? '\n' : ' - '}${price}/mo`;
            return html`<div class=${classMap({ 'plan-card-wrapper': this.plans.length > 1 })}>
                <lh-container gutters="none" theme="transparent" spacing="none">
                    <div class="plan-card">
                        <lh-typography type="text" size="md">
                            <strong
                                class=${classMap({ 'card-title': !this.isMobile, 'card-title-mobile': this.isMobile })}
                                >${title}</strong
                            >
                        </lh-typography>
                        <button
                            class="remove-btn"
                            aria-label="Remove compare"
                            @click=${() => {
                                this.dispatchEvent(
                                    makeCustomEvent(EVENT.COMPARE_BAR_REMOVAL, { planCode: plan.planCode })
                                );
                            }}
                        >
                            <lh-icon name="x" size="md"></lh-icon>
                        </button>
                    </div>
                </lh-container>
            </div>`;
        });
    }

    get isMobile() {
        return this.breakpoint === BREAKPOINT.PHONE || this.breakpoint === BREAKPOINT.TABLET;
    }

    render() {
        return html` <lh-container gutters="none" theme="secondary-dark" spacing="none" content-width="fluid">
            <div class=${classMap({ wrapper: true, 'wrapper-mobile': this.isMobile })}>
                <div class=${classMap({ plans: true, 'plans-mobile': this.isMobile })}>
                    <div class=${classMap({ 'top-mobile': this.isMobile })}>
                        <lh-typography type="text" size="lg">
                            <span class="numbers">${this.plans.length} of 3${this.isMobile ? ' Plans' : nothing}</span>
                        </lh-typography>
                        ${this.isMobile
                            ? html` <span class="numbers">
                                  <lh-icon
                                      tabindex="1"
                                      name="x"
                                      accessible-label="Close compare bar"
                                      @click=${() => {
                                          this.plans.forEach(({ planCode }) => {
                                              this.dispatchEvent(
                                                  makeCustomEvent(EVENT.COMPARE_BAR_REMOVAL, { planCode })
                                              );
                                          });
                                      }}
                                  >
                                  </lh-icon>
                              </span>`
                            : nothing}
                    </div>
                    <div class="cards">${this.planCards}</div>
                </div>
                <div class="compare-btn">
                    <lh-button
                        tabindex="1"
                        .disabled=${this.plans.length < 2}
                        full-width=${this.isMobile || nothing}
                        icon="caret-right"
                        icon-position="right"
                        variant="filled"
                        analytics-id="rate-quote:${this.source}:u65:plans-${getAnalyticPageDescription(
                            this.filters
                        )}:compare-plans:button-filled"
                        @click=${() => {
                            this.dispatchEvent(
                                makeCustomEvent(EVENT.CHANGE_PAGE, {
                                    page: PAGE.COMPARE,
                                    planIds: this.plans.map((p) => p.planId),
                                })
                            );
                        }}
                        >Compare</lh-button
                    >
                </div>
            </div>
        </lh-container>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'rq-compare-bar': CompareBar;
    }
}
